import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SimpleBar from 'simplebar-react'
import {
  XClose,
  ArrowLeft,
  Clock,
  Copy01,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download01,
} from '@untitled-ui/icons-react'
import { BillingService } from '../../billing.service'
import { useCompany } from '../../Context'
import { downloadInvoiceDocument } from '../../invoiceDocument'
import { formatIDR, invoiceStatusBadge } from '../../billing.format'
import PaymentQr from '@src/components/PaymentQr'

// Invoice payment detail — slide-in panel beside the company DetailSlider,
// opened by clicking a row in the "Billing and invoicing" table.
//
// Maps the Figma "Payment" frame (node 11840:43285). Adapted to our Xendit
// *Invoice API* (hosted checkout) reality: there is NO fixed VA number at
// create time, so instead of a copyable VA number we expose the hosted
// `checkout_url` (a copyable "Payment link" + a "Pay now" button). State is
// driven by invoice.status: unpaid → pay flow, paid → receipt, canceled /
// expired → terminal info.

function formatDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  return `${date} • ${time}`
}

// Human "time left" until expiry. > 1h → "5d 12h" / "3h 20m"; < 1h → "08:21";
// past → null (caller renders "Expired").
function formatTimeLeft(ms) {
  if (ms <= 0) return null
  const totalSec = Math.floor(ms / 1000)
  const days = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function paymentChannelLabel(detail) {
  const ch = detail?.payment_channel
  const method = detail?.payment_method
  // Only bank-transfer channels are Virtual Accounts. For e-wallet / QRIS /
  // card (forwarded to Xendit but not yet offered in the FE) show the channel
  // as-is so we don't mislabel e.g. "OVO Virtual account".
  const isBankTransfer = !method || method === 'BANK_TRANSFER'
  if (!ch) return isBankTransfer ? 'Virtual account' : method
  return isBankTransfer ? `${ch} Virtual account` : ch
}

function StatusBadge({ status }) {
  const { cls, label } = invoiceStatusBadge(status)
  return (
    <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  )
}

function invoiceTypeLabel(inv) {
  if (!inv) return ''
  if (inv.invoice_type === 'subscription') return 'Platform fee'
  if (inv.invoice_type === 'topup') return `Top up ${inv.quota_amount} enquiry`
  return 'Quota purchase'
}

// The "Pay before" / "Paid" / terminal hero card at the top of the panel.
function HeroCard({ invoice, detail, timeLeft }) {
  const status = invoice?.status
  if (status === 'paid') {
    return (
      <div className="relative rounded-xl border border-gray-light/200 bg-white px-3 py-6 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/100">
            <CheckCircle className="h-6 w-6 text-success/600" />
          </div>
          <div className="flex flex-1 flex-col">
            <p className="text-lg font-semibold text-gray-light/900">Payment received</p>
            <p className="text-sm text-gray-light/600">
              {detail?.paid_at || invoice?.paid_at
                ? `Paid on ${formatDateTime(detail?.paid_at || invoice?.paid_at)}`
                : 'Paid'}
            </p>
          </div>
        </div>
      </div>
    )
  }
  if (status === 'canceled' || status === 'expired') {
    const isCanceled = status === 'canceled'
    const Icon = isCanceled ? XCircle : Clock
    return (
      <div className="relative rounded-xl border border-gray-light/200 bg-white px-3 py-6 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-light/100">
            <Icon className="h-6 w-6 text-gray-light/500" />
          </div>
          <div className="flex flex-1 flex-col">
            <p className="text-lg font-semibold text-gray-light/900">
              {isCanceled ? 'Payment canceled' : 'Payment expired'}
            </p>
            <p className="text-sm text-gray-light/600">
              {isCanceled
                ? 'This invoice has been canceled.'
                : 'The payment session has expired.'}
            </p>
          </div>
        </div>
      </div>
    )
  }
  // unpaid. The live countdown is a *payment-session* concept, so it tracks the
  // Xendit checkout expiry (detail.expired_at) only. due_date (~7 days, the
  // invoice due) is shown as the date text fallback but never as a countdown —
  // ticking down a fabricated 7-day deadline would mislead.
  const sessionExpiry = detail?.expired_at || null
  const displayDate = sessionExpiry || invoice?.due_date
  return (
    <div className="relative rounded-xl border border-gray-light/200 bg-white px-3 py-6 shadow-xs">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-warning/600">
          <Clock className="h-6 w-6 text-white" />
        </div>
        <div className="flex flex-1 flex-col">
          <p className="text-lg font-semibold text-gray-light/900">Pay before</p>
          <p className="text-sm text-gray-light/600">{formatDateTime(displayDate)}</p>
        </div>
      </div>
      {sessionExpiry ? (
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-2xl border-[1.5px] border-warning/600 px-2 py-0.5 text-xs font-medium text-warning/700">
          {timeLeft || 'Expired'}
        </span>
      ) : null}
    </div>
  )
}

function StatusRow({ label, children, last }) {
  return (
    <div
      className={`flex h-[60px] items-center justify-between px-5 ${
        last ? '' : 'border-b border-gray-light/200'
      }`}
    >
      <span className="text-sm text-gray-light/600">{label}</span>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-light/900">{children}</div>
    </div>
  )
}

function InvoicePaymentPanel({ companyId, invoiceId, onClose }) {
  const { getCompany, companyDetail, refreshBilling } = useCompany()
  const [invoice, setInvoice] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [canceling, setCanceling] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [copiedKey, setCopiedKey] = useState(null)
  const [now, setNow] = useState(() => Date.now())

  const load = useCallback(
    async ({ silent, isActive } = {}) => {
      if (!companyId || !invoiceId) return
      if (!silent) setLoading(true)
      setError(null)
      try {
        const res = await BillingService.getInvoiceDetail(companyId, invoiceId)
        // Guard against an out-of-order response landing after the panel moved
        // on (e.g. invoiceId changed mid-flight) — drop stale results.
        if (isActive && !isActive()) return
        const data = res?.data ?? res
        setInvoice(data || null)
      } catch (err) {
        if (isActive && !isActive()) return
        setError(err?.response?.data?.message || err.message || 'Failed to load invoice')
      } finally {
        if (!silent && (!isActive || isActive())) setLoading(false)
      }
    },
    [companyId, invoiceId],
  )

  useEffect(() => {
    let active = true
    load({ isActive: () => active })
    return () => {
      active = false
    }
  }, [load])

  // Tick every second so the "time left" badge counts down live (only matters
  // while unpaid; cheap enough to always run while the panel is open).
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  const detail = invoice?.payment_detail || null
  const status = invoice?.status

  // Auto-poll while unpaid so a payment confirmed via webhook flips the panel
  // to "Paid" on its own (no manual "Refresh status"). Stops once terminal.
  useEffect(() => {
    if (status !== 'unpaid') return undefined
    const t = setInterval(() => load({ silent: true }), 6000)
    return () => clearInterval(t)
  }, [status, load])

  // When the status transitions away from unpaid (paid / expired / canceled),
  // propagate to the Billing tab + company list so they refresh too.
  const prevStatusRef = useRef(null)
  useEffect(() => {
    if (status && prevStatusRef.current === 'unpaid' && status !== 'unpaid') {
      refreshBilling?.()
      getCompany?.()
    }
    prevStatusRef.current = status
  }, [status, refreshBilling, getCompany])

  const timeLeft = useMemo(() => {
    // Countdown tracks the Xendit checkout-session expiry only (see HeroCard).
    const expiry = detail?.expired_at
    if (!expiry) return null
    return formatTimeLeft(new Date(expiry).getTime() - now)
  }, [detail, now])

  const handleRefresh = async () => {
    setRefreshing(true)
    await load({ silent: true })
    setRefreshing(false)
  }

  const handlePayNow = () => {
    if (detail?.checkout_url) window.open(detail.checkout_url, '_blank', 'noopener,noreferrer')
  }

  // Copy arbitrary text (VA number / checkout link), keyed so only the clicked
  // row shows "Copied!". Falls back to execCommand on insecure (plain-HTTP)
  // contexts where the async Clipboard API is unavailable (on-prem deploy).
  const handleCopy = async (text, key) => {
    if (!text) return
    let ok = false
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        ok = true
      }
    } catch {
      ok = false
    }
    if (!ok) {
      try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        ok = document.execCommand('copy')
        document.body.removeChild(ta)
      } catch {
        ok = false
      }
    }
    if (ok) {
      setCopiedKey(key)
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500)
    }
  }

  const handleCancel = async () => {
    setCanceling(true)
    setError(null)
    try {
      await BillingService.cancelInvoice(companyId, invoiceId)
      setConfirmCancel(false)
      await load({ silent: true })
      // Reflect the new status in the Billing tab + parent company list.
      refreshBilling?.()
      getCompany?.()
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to cancel payment')
    } finally {
      setCanceling(false)
    }
  }

  const isUnpaid = status === 'unpaid'
  const hasCheckout = Boolean(detail?.checkout_url)
  const hasQr = Boolean(detail?.qr_string || detail?.qr_url)

  return (
    <div className="flex h-screen w-[420px] flex-col bg-white shadow-xl">
      <header className="relative flex flex-col gap-4 border-b border-gray-100 px-6 pb-6 pt-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-[12px] top-[12px] flex h-10 w-10 items-center justify-center rounded-lg p-2 text-gray-400 hover:bg-gray-50 active:bg-gray-100"
        >
          <XClose size={20} stroke="currentColor" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1.5 self-start text-sm font-semibold text-gray-light/600 hover:text-gray-light/800"
        >
          <ArrowLeft size={18} stroke="currentColor" />
          Back
        </button>
        <div className="flex items-end justify-between gap-3">
          <div className="flex flex-col gap-1">
            <p className="text-xl font-semibold text-gray-900">Payment</p>
            <p className="text-sm text-gray-500">All transactions are secure and encrypted.</p>
          </div>
          {invoice ? (
            <button
              type="button"
              onClick={() => downloadInvoiceDocument(invoice, companyDetail)}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-light/300 bg-white px-3 py-2 text-sm font-semibold text-gray-light/700 shadow-xs hover:bg-gray-light/50"
            >
              <Download01 size={16} stroke="currentColor" />
              {status === 'paid' ? 'Receipt' : 'Invoice'}
            </button>
          ) : null}
        </div>
      </header>

      <section className="flex-1 overflow-hidden">
        <SimpleBar forceVisible="y" style={{ height: '100%' }}>
          <div className="flex flex-col gap-6 px-6 py-6">
            {error ? (
              <div className="rounded-md border border-error/200 bg-error/50 p-3 text-sm text-error/700">
                {error}
              </div>
            ) : null}

            {loading ? (
              <>
                <div className="h-24 animate-pulse rounded-xl bg-gray-light/100" />
                <div className="h-48 animate-pulse rounded-xl bg-gray-light/100" />
              </>
            ) : !invoice ? (
              <p className="text-sm text-gray-light/600">Invoice not found.</p>
            ) : (
              <>
                {/* ── Hero (pay before / paid / terminal) ── */}
                <HeroCard invoice={invoice} detail={detail} timeLeft={timeLeft} />

                {/* ── In-portal QRIS — the QR the payer scans ── */}
                {isUnpaid && hasQr ? (
                  <PaymentQr qrString={detail?.qr_string} qrUrl={detail?.qr_url} />
                ) : null}

                {/* ── Payment status card ── */}
                <div className="overflow-hidden rounded-xl border border-gray-light/200 bg-gray-light/25 shadow-xs">
                  <div className="flex items-center justify-between px-5 pb-2 pt-3">
                    <p className="flex-1 text-sm font-semibold text-gray-900">Payment status</p>
                    <StatusBadge status={status} />
                  </div>
                  <div className="rounded-xl border border-gray-light/200 bg-white shadow-xs">
                    <StatusRow label="Invoice">{invoice.invoice_number}</StatusRow>
                    <StatusRow label="Description">{invoiceTypeLabel(invoice)}</StatusRow>
                    <StatusRow label="Payment method">
                      {isUnpaid && !detail ? '—' : paymentChannelLabel(detail)}
                    </StatusRow>
                    {isUnpaid && detail?.va_number ? (
                      <StatusRow label="VA number">
                        <button
                          type="button"
                          onClick={() => handleCopy(detail.va_number, 'va')}
                          className="flex items-center gap-1.5 text-gray-light/900 hover:text-brand/700"
                          title="Copy VA number"
                        >
                          <span className="max-w-[200px] truncate tabular-nums">
                            {copiedKey === 'va' ? 'Copied!' : detail.va_number}
                          </span>
                          <Copy01 size={15} stroke="currentColor" />
                        </button>
                      </StatusRow>
                    ) : null}
                    {isUnpaid && hasCheckout ? (
                      <StatusRow label="Payment link">
                        <button
                          type="button"
                          onClick={() => handleCopy(detail.checkout_url, 'link')}
                          className="flex items-center gap-1.5 text-brand/700 hover:underline"
                          title={detail.checkout_url}
                        >
                          <span className="max-w-[150px] truncate">
                            {copiedKey === 'link'
                              ? 'Copied!'
                              : detail.checkout_url.replace(/^https?:\/\//, '')}
                          </span>
                          <Copy01 size={15} stroke="currentColor" />
                        </button>
                      </StatusRow>
                    ) : null}
                    {status === 'paid' ? (
                      <StatusRow label="Paid at">
                        {formatDateTime(detail?.paid_at || invoice.paid_at)}
                      </StatusRow>
                    ) : null}
                    <StatusRow label="Total amount" last>
                      {formatIDR(invoice.total_idr)}
                    </StatusRow>
                  </div>
                </div>

                {/* ── Important notes (only while payable) ── */}
                {isUnpaid ? (
                  <div className="rounded-xl border border-gray-light/200 bg-white p-6 shadow-xs">
                    <div className="flex items-center gap-1">
                      <AlertCircle size={16} className="text-gray-light/600" />
                      <p className="text-sm font-semibold text-gray-light/700">Important</p>
                    </div>
                    <ul className="mt-1 list-disc pl-5 text-sm text-gray-light/600">
                      {hasQr ? (
                        <li>Scan the QR above with any QRIS-capable bank or e-wallet app.</li>
                      ) : (
                        <li>Virtual Account payment must be made from the selected bank.</li>
                      )}
                      <li>Quota is added automatically once the payment is verified.</li>
                    </ul>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </SimpleBar>
      </section>

      {/* ── Actions (only while unpaid) ── */}
      {!loading && invoice && isUnpaid ? (
        <div className="flex flex-col gap-3 border-t border-gray-light/200 px-6 py-6">
          {confirmCancel ? (
            <div className="rounded-lg border border-error/200 bg-error/50 p-3">
              <p className="text-sm font-semibold text-error/700">Cancel this payment?</p>
              <p className="mt-1 text-sm text-error/700">
                This invoice will be marked canceled and can no longer be paid.
              </p>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmCancel(false)}
                  disabled={canceling}
                  className="rounded-lg border border-gray-light/300 bg-white px-3 py-2 text-sm font-semibold text-gray-light/700 hover:bg-gray-light/50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={canceling}
                  className="rounded-lg bg-error/600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-error/700 disabled:opacity-50"
                >
                  {canceling ? 'Canceling…' : 'Yes, cancel'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handlePayNow}
                  disabled={!hasCheckout}
                  title={hasCheckout ? undefined : 'No active payment session'}
                  className="flex-1 rounded-lg bg-brand/600 px-3 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-brand/700 disabled:opacity-50"
                >
                  Pay now
                </button>
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="flex-1 rounded-lg border border-brand/300 bg-white px-3 py-2.5 text-sm font-semibold text-brand/700 shadow-xs hover:bg-brand/50 disabled:opacity-50"
                >
                  {refreshing ? 'Refreshing…' : 'Refresh status'}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setConfirmCancel(true)}
                className="w-full rounded-lg border border-error/300 bg-white px-3 py-2.5 text-sm font-semibold text-error/700 shadow-xs hover:bg-error/50"
              >
                Cancel payment
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default InvoicePaymentPanel
